import ReactGA from 'react-ga4';

let initialized = false;
let activeMeasurementId = null;

// Browsers Fer uses as admin are kept out of the stats (with ~40 visitors a
// month, his own testing was a real share of them). Set once an admin session
// shows up on a device and kept after sign-out. `?analytics=on` / `=off` in any
// URL flips it by hand, e.g. to watch your own visit in GA's Realtime report.
const INTERNAL_DEVICE_KEY = 'rivals-analytics-internal';

const readInternalFlag = () => {
  try {
    return localStorage.getItem(INTERNAL_DEVICE_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const writeInternalFlag = (internal) => {
  try {
    if (internal) localStorage.setItem(INTERNAL_DEVICE_KEY, '1');
    else localStorage.removeItem(INTERNAL_DEVICE_KEY);
  } catch (error) {
    // Storage unavailable: the opt-out just won't persist on this device.
  }
};

// Fer's devices: the header shows the admin sign-in only there (everyone else
// can still sign in at /admin/articles).
export const isInternalDevice = readInternalFlag;

// Only the live site counts. localhost (our testing, and the headless
// screenshot runs that start with empty storage) and Firebase preview URLs
// were outnumbering real visitors in GA, so they never send anything.
const isLiveSite = () => /(^|\.)rivalsbase\.com$/.test(window.location.hostname || '');

// gtag's own kill switch, checked on every hit.
const setTrackingDisabled = (disabled) => {
  if (activeMeasurementId) window[`ga-disable-${activeMeasurementId}`] = disabled;
};

export const initializeAnalytics = (measurementId) => {
  if (!measurementId || initialized) return;
  activeMeasurementId = measurementId;

  const override = new URLSearchParams(window.location.search).get('analytics');
  if (override === 'on') writeInternalFlag(false);
  if (override === 'off') writeInternalFlag(true);
  setTrackingDisabled(readInternalFlag() || !isLiveSite());

  ReactGA.initialize(measurementId, {
    gaOptions: {
      anonymizeIp: true,
    },
    // Page views are sent by hand (trackPageView), once per route and only
    // after the page has its real title; gtag's automatic one on load counted
    // every landing twice.
    gtagOptions: {
      send_page_view: false,
    },
  });

  initialized = true;
};

export const excludeThisDeviceFromAnalytics = () => {
  if (readInternalFlag()) return;
  writeInternalFlag(true);
  setTrackingDisabled(true);
};

// Sends the full URL and the current document.title explicitly: GA4 reports
// pages by page_location, and without page_title it records whatever title
// the tab had a moment earlier.
export const trackPageView = (path = window.location.pathname + window.location.search) => {
  if (!initialized) return;

  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: document.title,
    location: `${window.location.origin}${path}`,
  });
};

export const trackEvent = (action, params = {}) => {
  if (!initialized) return;

  ReactGA.event(action, params);
};

// A reader pressed play on a YouTube video inside an article.
export const trackVideoPlay = (articleId, videoId) => {
  trackEvent('video_play', { article_id: articleId || '', video_id: videoId });
};

// `results` is how many matches the search returned. A zero also goes out as
// its own `search_no_results` event, so "what are people looking for that the
// site doesn't have" is one click away in GA's Events report.
export const trackSearch = (query, { area, results } = {}) => {
  const params = {
    search_term: query,
    search_area: area,
    results_count: results,
    event_category: 'engagement',
  };
  trackEvent('search', params);
  if (results === 0) trackEvent('search_no_results', params);
};

// Clicks that move a reader around the site (which blocks actually get used).
export const trackContentClick = (placement, { articleId, position } = {}) => {
  trackEvent('content_click', {
    placement,
    article_id: articleId,
    position,
    event_category: 'navigation',
  });
};

export const trackPageNotFound = (kind) => {
  trackEvent('page_not_found', {
    page_path: window.location.pathname,
    not_found_kind: kind,
    page_referrer: document.referrer || '(none)',
    event_category: 'errors',
  });
};

// Core Web Vitals from real visitors (web-vitals v2 metric objects), in the
// shape Google's web-vitals docs suggest for GA4: event named after the metric.
export const trackWebVital = ({ name, value, delta, id }) => {
  const round = (number) => Math.round(name === 'CLS' ? number * 1000 : number);
  trackEvent(name, {
    value: round(delta),
    metric_value: round(value),
    metric_id: id,
    event_category: 'web_vitals',
    non_interaction: true,
  });
};

// Uncaught JS errors as GA4 `exception` events, capped per page load so one
// broken loop can't flood the property. Cross-origin "Script error." carries
// no detail, and errors from browser extensions aren't the site's.
const MAX_ERRORS_PER_PAGE = 5;
let reportedErrors = 0;

export const installErrorTracking = () => {
  const report = (description) => {
    if (!description || reportedErrors >= MAX_ERRORS_PER_PAGE) return;
    reportedErrors += 1;
    trackEvent('exception', { description: description.slice(0, 150), fatal: false });
  };

  window.addEventListener('error', (event) => {
    if (!event.message || event.message === 'Script error.') return;
    if (/^(chrome|moz|safari)-extension:/.test(event.filename || '')) return;
    const file = String(event.filename || '').split('/').pop();
    report(`${event.message}${file ? ` @ ${file}:${event.lineno}` : ''}`);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    report(`Unhandled rejection: ${reason?.message || String(reason)}`);
  });
};

// Microsoft Clarity (heatmaps + anonymised session recordings). Same opt-out
// as GA: devices marked internal never load it. Visitors from the EEA/UK/CH
// get a cookieless, limited version until the site collects consent.
export const initializeClarity = (projectId) => {
  if (!projectId || readInternalFlag() || !isLiveSite() || window.clarity) return;

  window.clarity = function clarity(...args) {
    (window.clarity.q = window.clarity.q || []).push(args);
  };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${projectId}`;
  document.head.appendChild(script);
};

export const trackLanguageChange = (language) => {
  trackEvent('language_change', {
    language,
    event_category: 'preferences',
  });
};

export const trackHeroInteraction = (heroId, action) => {
  trackEvent(action, {
    hero_id: heroId,
    event_category: 'hero_interaction',
  });
};

export const trackAdminAction = (action) => {
  trackEvent(action, {
    event_category: 'admin',
  });
};

export const trackVote = (heroId, teamUpIndex, voted) => {
  trackEvent(voted ? 'vote_cast' : 'vote_removed', {
    hero_id: heroId,
    team_up_index: teamUpIndex,
    event_category: 'engagement',
  });
};

export const trackArticleLike = (articleId, liked) => {
  trackEvent(liked ? 'article_liked' : 'article_unliked', {
    article_id: articleId,
    event_category: 'article_engagement',
  });
};

export const trackArticleShare = (articleId, method) => {
  trackEvent('article_shared', {
    article_id: articleId,
    method,
    event_category: 'article_engagement',
  });
};

export const trackCategoryFilter = (categoryId) => {
  trackEvent('category_filter', {
    category_id: categoryId || 'all',
    event_category: 'blog_engagement',
  });
};

// How far into the article body a reader got (25/50/75/100), once each per visit.
export const trackArticleReadProgress = (articleId, percent) => {
  trackEvent('article_read_progress', {
    article_id: articleId,
    percent,
    event_category: 'article_engagement',
  });
};

export const trackSocialClick = (platform, placement) => {
  trackEvent('social_click', {
    platform,
    placement,
    event_category: 'outbound',
  });
};

export const trackFeedbackWidget = (action, feedbackType) => {
  trackEvent(action, {
    feedback_type: feedbackType,
    event_category: 'feedback',
  });
};
